import { memo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { IosLockScreenNotification } from '@/components/campaign-composer/ios-lock-screen-notification';
import {
  STUDIO_ENTRANCE_DELAY_MS,
  formatLockScreenClock,
  formatLockScreenDate,
  useDelayedFlag,
} from '@/components/campaign-composer/korza-studio';
import { RollingClock } from '@/components/campaign-composer/rolling-clock';
import { LongArrow } from '@/components/brand/long-arrow';
import { KorzaStudioStage } from '@/components/korza-studio/korza-studio-stage';
import {
  arrivalLabel,
  timelineArrowWidth,
  welcomeArrival,
  welcomeStudioAnchor,
  type StudioFocus,
} from '@/components/welcome-notification/welcome-studio';
import { useI18nContext } from '@/i18n/i18n-react';
import type { WelcomeDelay } from '@/lib/app-editor/welcome-notification-form';
import { LockScreenScene } from '@/pages/onboarding/mockup/scenes/lock-screen-scene';

// IPhoneMockup's default; the notification is scaled from iOS points to it.
const SCREEN_WIDTH = 300;

const LABEL_CLASS =
  'text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#5B6371]';
const TIME_CLASS = 'text-[13px] font-semibold tabular-nums text-[#15191E]';

/**
 * Welcome notification canvas in the Korza Studio (KorzaStudioStage): an
 * iPhone on the lock screen and the iOS 26 push, cut and wrapped as on the
 * phone. The app is installed at 9:41 and the push arrives `delayMinutes`
 * later: the clock rolls (and the date moves) to then, and the pill above
 * spells it out. On a short stage the device zooms in, framed on the push
 * or, while the delay is edited, on the clock.
 */
export const WelcomeStudioPreview = memo(function WelcomeStudioPreview({
  title,
  body,
  iconUrl,
  locale,
  delayMinutes,
  active,
  focus,
  onNotificationPress,
}: {
  title: string;
  body: string;
  iconUrl: string | null | undefined;
  locale: string;
  delayMinutes: WelcomeDelay;
  /** Off: the push still shows (it is being written), with a badge. */
  active: boolean;
  focus: StudioFocus;
  /** Tap on the push: back to editing its text. */
  onNotificationPress?: () => void;
}) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion() ?? false;
  const visible = useDelayedFlag(
    true,
    reduceMotion ? 0 : STUDIO_ENTRANCE_DELAY_MS
  );

  const arrival = welcomeArrival(delayMinutes);
  const clock = formatLockScreenClock(arrival, locale);
  const dateLabel = formatLockScreenDate(arrival, locale);
  const label = arrivalLabel(delayMinutes);
  const arrivalText =
    label.kind === 'now'
      ? LL.welcomeEditor.studio.arrivesNow()
      : label.kind === 'sameDay'
        ? clock
        : label.days === 1
          ? LL.welcomeEditor.studio.arrivesTomorrow({ time: clock })
          : LL.welcomeEditor.studio.arrivesInDays({
              days: label.days,
              time: clock,
            });

  return (
    <KorzaStudioStage
      testId="welcome-studio"
      label={LL.welcomeEditor.studio.label()}
      focus={focus}
      anchor={welcomeStudioAnchor(focus)}
      sceneKey="welcome-lock-screen"
      chromeBaked
      offLabel={active ? null : LL.welcomeEditor.canvas.off()}
      pills={({ clear }) => (
        // Framed on the push, the pill would sit on the screen itself.
        <motion.p
          data-testid="welcome-timeline"
          initial={false}
          animate={{ opacity: clear ? 1 : 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.3 }}
          aria-hidden={!clear || undefined}
          className="flex h-8 items-center gap-3 rounded-full bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_0_0_1px_rgba(15,23,42,0.06)]"
        >
          <span className={LABEL_CLASS}>
            {LL.welcomeEditor.studio.installed()}
          </span>
          <span className={TIME_CLASS}>
            {formatLockScreenClock(welcomeArrival(0), locale)}
          </span>
          <LongArrow
            className="text-[#155DFC]"
            lineClassName="duration-700"
            // The wait, drawn: longer delays, longer arrow.
            lineStyle={{ width: timelineArrowWidth(delayMinutes) }}
          />
          <span className={LABEL_CLASS}>
            {LL.welcomeEditor.studio.arrives()}
          </span>
          <span className={TIME_CLASS}>{arrivalText}</span>
        </motion.p>
      )}
    >
      <div className="relative h-full w-full">
        <div aria-hidden className="h-full w-full">
          <LockScreenScene
            dateLabel={
              <motion.span
                key={dateLabel}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.5 }}
                className="inline-block"
              >
                {dateLabel}
              </motion.span>
            }
            clock={<RollingClock value={clock} />}
          />
        </div>
        <IosLockScreenNotification
          title={title}
          body={body}
          titlePlaceholder={LL.notificationPreview.titlePlaceholder()}
          bodyPlaceholder={LL.notificationPreview.bodyPlaceholder()}
          timeLabel={LL.notificationPreview.now()}
          iconUrl={iconUrl}
          screenWidth={SCREEN_WIDTH}
          visible={visible}
          reduceMotion={reduceMotion}
          onPress={onNotificationPress}
          pressLabel={LL.notificationPreview.editMessage()}
        />
      </div>
    </KorzaStudioStage>
  );
});
